/*
 * BCD variant picker — 1 to 3 options (Color / Style / Size) for the BCD PDP (template suffix "bcd").
 * Markup: snippets/pdp-bcd-variant-picker.liquid. Pure selection logic is exported for
 * tests/pdp-bcd-variant-picker.test.js (run: `node --test` from the repo root); DOM wiring runs only in the browser.
 */
(function (root) {
  'use strict';

  // Variants with `value` at `key` that also match the current selection on every other key.
  function matching(variants, selected, keys, key, value) {
    return variants.filter(function (v) {
      if (v[key] !== value) return false;
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        if (k !== key && selected[k] != null && v[k] !== selected[k]) return false;
      }
      return true;
    });
  }

  // 'available' | 'soldout' | 'none' (no variant exists for this value + the other selections)
  function optionState(variants, selected, keys, key, value) {
    var m = matching(variants, selected, keys, key, value);
    if (!m.length) return 'none';
    return m.some(function (v) { return v.available; }) ? 'available' : 'soldout';
  }

  // Variant for the selection after the shopper changed `changedKey`. An exact match wins
  // (even sold out). Otherwise keep the changed value plus as many other selections as
  // possible, preferring in-stock. Null only if no variant has the changed value.
  function resolveVariant(variants, selected, keys, changedKey) {
    var exact = matching(variants, selected, keys, changedKey, selected[changedKey]);
    if (exact.length) return exact[0];
    var best = null;
    var bestScore = -1;
    variants.forEach(function (v) {
      if (v[changedKey] !== selected[changedKey]) return;
      var score = v.available ? 1 : 0;
      keys.forEach(function (k) { if (k !== changedKey && v[k] === selected[k]) score += 2; });
      if (score > bestScore) { best = v; bestScore = score; }
    });
    return best;
  }

  // Image for a Color/Style button: the matching variant's image, else any variant with that value.
  function imageForValue(variants, selected, keys, key, value) {
    var m = matching(variants, selected, keys, key, value);
    for (var i = 0; i < m.length; i++) if (m[i].image) return m[i].image;
    for (var j = 0; j < variants.length; j++) {
      if (variants[j][key] === value && variants[j].image) return variants[j].image;
    }
    return null;
  }

  // ── Any-order picking (Andrew 2026-10-23; replaces the 10-19 top-down cascade) ──
  // Every group is clickable first. A value is judged against the picks in ALL other groups; with nothing
  // picked elsewhere it reads 'available', so nothing is crossed out on load (not even on an all-sold-out
  // product). `order` = option keys in display order (Color → Style → Size).
  // A single-option product has no other groups: judge the value on its own (sold-out sizes cross out on load).
  function peerState(variants, selected, order, key, value) {
    if (order.length === 1) return optionState(variants, {}, order, key, value);
    var others = {}, any = false;
    order.forEach(function (k) {
      if (k !== key && selected[k] != null) { others[k] = selected[k]; any = true; }
    });
    if (!any) return 'available';
    return optionState(variants, others, order, key, value);
  }

  // After the shopper picks `changedKey` (in any group), fix up the OTHER groups. The new pick always stays.
  // 1) Keep other picks in display order while a variant still exists with the new pick + the picks kept so
  //    far; clear the rest (Black + XS, then BPI where Black has no BPI → Black cleared, XS kept).
  // 2) If the kept picks land on a sold-out combination, move ONE group — the lowest in display order (Size
  //    first) — to its first in-stock value, if any. All sold out → picks stay put.
  // `valuesByKey` = each group's values in button order. Returns a new selection.
  function reconcile(variants, selected, order, changedKey, valuesByKey) {
    var out = {};
    order.forEach(function (k) { out[k] = null; });
    out[changedKey] = selected[changedKey];
    order.forEach(function (k) {
      if (k === changedKey || selected[k] == null) return;
      if (optionState(variants, out, order, k, selected[k]) !== 'none') out[k] = selected[k];
    });
    for (var i = order.length - 1; i >= 0; i--) {
      var k = order[i];
      if (k === changedKey || out[k] == null) continue;
      if (peerState(variants, out, order, k, out[k]) !== 'soldout') continue;
      var vals = valuesByKey[k] || [];
      for (var j = 0; j < vals.length; j++) {
        if (peerState(variants, out, order, k, vals[j]) === 'available') { out[k] = vals[j]; return out; }
      }
    }
    return out;
  }

  // The variant for a COMPLETE selection (every key picked), else null.
  function exactVariant(variants, selected, order) {
    for (var i = 0; i < order.length; i++) if (selected[order[i]] == null) return null;
    for (var j = 0; j < variants.length; j++) {
      var v = variants[j], ok = true;
      for (var n = 0; n < order.length; n++) if (v[order[n]] !== selected[order[n]]) { ok = false; break; }
      if (ok) return v;
    }
    return null;
  }

  var api = { optionState: optionState, resolveVariant: resolveVariant, imageForValue: imageForValue,
    peerState: peerState, reconcile: reconcile, exactVariant: exactVariant };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  root.SBABcdPicker = api;

  // Mirrors switchGalleryToVariant() in pdp-dive-computer.js (not exposed globally there).
  function switchGallery(variantId) {
    var mainWrap = document.querySelector('[data-pdp-gallery-main]');
    if (!mainWrap) return;
    var target = null;
    var imgs = mainWrap.querySelectorAll('.pdp-product-gallery__main-img');
    imgs.forEach(function (img) {
      var ids = img.getAttribute('data-variant-ids');
      if (ids && ids.split(',').indexOf(String(variantId)) !== -1) target = img.getAttribute('data-index');
    });
    if (target === null) return;
    imgs.forEach(function (img) { img.classList.toggle('is-active', img.getAttribute('data-index') === target); });
    document.querySelectorAll('.pdp-product-gallery__thumb').forEach(function (t) {
      t.classList.toggle('is-active', t.getAttribute('data-index') === target);
    });
    // Desktop main image switches via the is-active class above; the mobile gallery is a separate
    // Swiper carousel built from the same product.images order (same index), so it needs its own
    // nudge. Swiper attaches the live instance to the container element as `.swiper` after init.
    var mobileWrap = document.querySelector('[data-pdp-gallery-mobile]');
    if (mobileWrap && mobileWrap.swiper && typeof mobileWrap.swiper.slideTo === 'function') {
      mobileWrap.swiper.slideTo(Number(target));
    }
  }

  function init() {
    var picker = document.querySelector('[data-bcd-picker]');
    var dataEl = document.getElementById('pdp-bcd-variant-data');
    if (!picker || !dataEl) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var form = picker.closest('form') || document;
    var input = picker.querySelector('[data-variant-id-input]');
    if (!input) return;
    var groups = Array.prototype.slice.call(picker.querySelectorAll('[data-bcd-opt-group]'));
    var keys = groups.map(function (g) { return g.getAttribute('data-option-key'); });
    var current = variants.filter(function (v) { return String(v.id) === input.value; })[0] || variants[0];
    var selected = {};
    // Multi-option products start with NOTHING picked (no ?variant= deep link): no cross-outs until the
    // shopper picks something. The Liquid sets data-preselect="false" for that case.
    var preselect = picker.getAttribute('data-preselect') !== 'false';
    keys.forEach(function (k) { selected[k] = preselect ? current[k] : null; });
    var valuesByKey = {};
    groups.forEach(function (g) {
      valuesByKey[g.getAttribute('data-option-key')] = Array.prototype.map.call(
        g.querySelectorAll('[data-option-value]'), function (b) { return b.getAttribute('data-option-value'); });
    });
    function displayOf(g, val) {
      var b = val == null ? null : g.querySelector('[data-option-value="' + (window.CSS && CSS.escape ? CSS.escape(val) : val) + '"]');
      return b ? (b.getAttribute('data-option-label') || val) : '';
    }

    var anyInStock = variants.some(function (v) { return v.available; });

    function render() {
      groups.forEach(function (g) {
        var key = g.getAttribute('data-option-key');
        var optName = g.getAttribute('data-option-display-name') || g.getAttribute('data-option-name') || '';
        var label = g.querySelector('[data-bcd-opt-selected]');
        if (label) label.textContent = selected[key] == null ? 'Select' : displayOf(g, selected[key]);
        g.querySelectorAll('[data-option-value]').forEach(function (btn) {
          var val = btn.getAttribute('data-option-value');
          var on = val === selected[key];
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          var ariaLabel = optName + ': ' + (btn.getAttribute('data-option-label') || val);
          // Every group is judged against the picks in the OTHER groups (any order). On an all-sold-out product
          // (e.g. pre-launch) greying tells the shopper nothing, so only impossible combinations are marked.
          var state = peerState(variants, selected, keys, key, val);
          if (state === 'soldout' && !anyInStock) state = 'available';
          btn.classList.toggle('is-unavailable', state === 'soldout');
          btn.classList.toggle('is-nocombo', state === 'none');
          if (state === 'soldout') ariaLabel += ' (sold out)';
          else if (state === 'none') ariaLabel += ' (not available in this combination)';
          btn.setAttribute('aria-label', ariaLabel);
          var img = btn.querySelector('.pdp-bcd-opt__img');
          if (img) {
            var src = imageForValue(variants, selected, keys, key, val);
            if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
          }
        });
      });
    }

    function setText(el, text) { if (el) el.textContent = text; }

    // Same targets pdp-dive-computer.js updates: price, ATC, Notify, Shop Pay, ?variant=, gallery.
    function commit(v) {
      input.value = v.id;
      setText(form.querySelector('[data-product-price]'), v.price);
      setText(form.querySelector('[data-atc-price]'), v.price);
      setText(form.querySelector('[data-product-compare]'), v.compare);
      setText(form.querySelector('[data-product-savings]'), v.savings ? 'Save ' + v.savings : '');
      setText(document.querySelector('[data-product-price-mobile]'), v.price);
      setText(document.querySelector('[data-product-compare-mobile]'), v.compare);
      setText(document.querySelector('[data-product-savings-mobile]'), v.savings ? 'Save ' + v.savings : '');
      var terms = document.querySelector('shopify-payment-terms');
      if (terms) terms.setAttribute('variant-id', v.id);
      var atc = form.querySelector('.pdp-atc-btn');
      var notify = form.querySelector('[data-pdp-notify]');
      var preorder = atc && atc.classList.contains('pdp-atc-btn--preorder');
      if (atc && !preorder) {
        atc.disabled = !v.available;
        setText(form.querySelector('[data-atc-label]'), v.available ? 'Add to Cart' : 'Sold Out');
      }
      if (notify) notify.hidden = preorder || v.available;
      try {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        // Keep whatever state other scripts stored on this history entry.
        window.history.replaceState(window.history.state, '', u.toString());
      } catch (e) {}
      switchGallery(v.id);
      // Let other listeners (apps, analytics, theme scripts) react like they would to a native variant change.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    picker.addEventListener('click', function (e) {
      // "Find your size ↓": the theme's global custom.js hijacks every a[href^="#"] click and animates to
      // offset().top - 80, which lands the Lift & Fit table under the ~94–131px fixed header. Stop the click
      // here (before it bubbles to that delegated document handler) and scroll natively, which honours
      // #pdp-bcd-fit's scroll-margin-top in pdp-bcd.css.
      var jump = e.target.closest('a.pdp-bcd-opt__help[href^="#"]');
      if (jump) {
        var dest = document.getElementById(jump.getAttribute('href').slice(1));
        if (dest) {
          e.preventDefault();
          e.stopPropagation();
          dest.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
      var btn = e.target.closest('[data-option-value]');
      if (!btn || !picker.contains(btn)) return;
      var key = btn.closest('[data-bcd-opt-group]').getAttribute('data-option-key');
      var val = btn.getAttribute('data-option-value');
      // Any value is clickable; reconcile() clears other picks that can't combine with it (dashed "no combo").
      selected[key] = val;
      selected = reconcile(variants, selected, keys, key, valuesByKey);
      render();
      var v = exactVariant(variants, selected, keys);
      if (v) { commit(v); return; }
      pending();
      // Partial pick (e.g. just a color): still show that color in the gallery.
      var look = variants.filter(function (x) {
        return x.image && keys.every(function (k) { return selected[k] == null || x[k] === selected[k]; });
      })[0];
      if (look) switchGallery(look.id);
    });

    // Incomplete selection: the hidden id still holds a fallback variant, so lock Add to Cart and say what's
    // missing ("Select a size") until every option is picked.
    function pending() {
      var missing = null;
      for (var i = 0; i < groups.length; i++) {
        if (selected[keys[i]] == null) { missing = groups[i]; break; }
      }
      var atc = form.querySelector('.pdp-atc-btn');
      var notify = form.querySelector('[data-pdp-notify]');
      var name = missing ? (missing.getAttribute('data-option-display-name') || missing.getAttribute('data-option-name') || 'options') : 'options';
      if (atc) {
        atc.disabled = true;
        setText(form.querySelector('[data-atc-label]'), 'Select ' + (/^[aeiou]/i.test(name) ? 'an ' : 'a ') + name.toLowerCase());
      }
      if (notify) notify.hidden = true;
      // "From <lowest in-stock price>" for the picks so far (plain price when they're all the same).
      var pool = variants.filter(function (x) {
        return keys.every(function (k) { return selected[k] == null || x[k] === selected[k]; });
      });
      var inStock = pool.filter(function (x) { return x.available; });
      if (inStock.length) pool = inStock;
      var min = null, minText = '', differ = false;
      pool.forEach(function (x) {
        var n = parseFloat(String(x.price).replace(/[^0-9.]/g, ''));
        if (isNaN(n)) return;
        if (min !== null && n !== min) differ = true;
        if (min === null || n < min) { min = n; minText = x.price; }
      });
      if (min !== null) {
        var label = (differ ? 'From ' : '') + minText;
        setText(form.querySelector('[data-product-price]'), label);
        setText(document.querySelector('[data-product-price-mobile]'), label);
        setText(form.querySelector('[data-atc-price]'), '');
        ['[data-product-compare]', '[data-product-savings]'].forEach(function (s) { setText(form.querySelector(s), ''); });
        ['[data-product-compare-mobile]', '[data-product-savings-mobile]'].forEach(function (s) { setText(document.querySelector(s), ''); });
      }
    }
    if (form !== document) {
      form.addEventListener('submit', function (e) {
        if (!exactVariant(variants, selected, keys)) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);
    }

    render();
    if (!exactVariant(variants, selected, keys)) pending();

    // Deep links (?variant=…) and a selected_or_first_available variant with its own image: show that
    // variant's image in the gallery on load. Run now for the desktop main image, and again on window
    // load because the mobile gallery Swiper is created by pdp-dive-computer.js on DOMContentLoaded (after
    // this deferred script). The load pass re-reads the input, so a click made before load still wins.
    function syncGallery() {
      var cur = variants.filter(function (v) { return String(v.id) === input.value; })[0];
      if (cur && cur.image) switchGallery(cur.id);
    }
    if (preselect) {
      syncGallery();
      if (document.readyState !== 'complete') window.addEventListener('load', syncGallery);
    }
  }

  // Feature cards: a landscape photo (≥1.2:1) gets the edge-to-edge photo-card treatment (.is-photo, pdp-bcd.css);
  // square cut-out PNGs keep the soft tile. Lives here because this is the BCD template's only script.
  function initPhotoFeatures() {
    if (!document.body.classList.contains('template__product--bcd')) return;
    document.querySelectorAll('.pdp-feature-card__image').forEach(function (img) {
      function check() {
        if (!img.naturalWidth) return;
        var card = img.closest('.pdp-feature-card');
        if (card) card.classList.toggle('is-photo', img.naturalWidth / img.naturalHeight >= 1.2);
      }
      // Lazy images off-screen can report complete with no size yet: always listen for load as well.
      check(); img.addEventListener('load', check);
    });
  }

  function start() { init(); initPhotoFeatures(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : this);
